import { useMemo, useCallback, useState, useEffect } from "react";
import { createGlobalState } from "react-use";
import { postMessageTo } from "hooks/PostMessage/Utils";
import {
    PdfHighlightAction,
    PdfHighlightActionEnum,
} from "hooks/PostMessage/Actions";
import { usePostMessageHandler } from "hooks/PostMessage/Handler";
import {
    ActivityType,
    ItemNoteType,
    ActivityDTO,
    IPdfShape,
    ItemNoteTypeEnum,
} from "data/types";

type PdfHighlightProps = {
    $type?: ItemNoteTypeEnum;
    shapes?: IPdfShape[];
    activityId: number;
    isReadonly: boolean;
};

const usePdfHighlight = createGlobalState<null | any>(null);

const usePdfHighlightActions = () => {
    const [target] = usePdfHighlight();
    const createPostMessage = useCallback(
        (action: PdfHighlightActionEnum) => (payload: any) => {
            if (target == null) {
                console.warn(`Target is null, ${action} will not be executed!`);
                return;
            }
            postMessageTo(action, payload, target);
        },
        [target]
    );
    return useMemo(
        () => ({
            handleLoad: createPostMessage(PdfHighlightAction.Load),
            handleUpsert: createPostMessage(PdfHighlightAction.Upsert),
            handleHighlight: createPostMessage(PdfHighlightAction.Highlight),
            handleDelete: createPostMessage(PdfHighlightAction.Delete),
            handleSelect: createPostMessage(PdfHighlightAction.Select),
        }),
        [createPostMessage]
    );
};

const usePdfHighlightActivityList = (
    items: ActivityDTO[],
    userId: number,
    setEdit: (activityId: number) => void,
    onAction: (action: string, itemIndex: number) => void,
    isReadOnly: boolean = false
) => {
    const [highlight, setHighlight] = useState<number | null>(null);
    const actions = usePdfHighlightActions();

    const pdfHighlights: PdfHighlightProps[] = useMemo(
        () =>
            items
                .filter(
                    (item) =>
                        item.extras &&
                        item.extras.$type === ItemNoteType.PdfHighlight
                )
                .map((item) => ({
                    activityId: item.id,
                    ...item.extras,
                    isReadonly: userId !== item.userId,
                })),
        [items, userId]
    );

    // Handle UI input from PDF
    usePostMessageHandler(
        useMemo(() => {
            const messageHandler = {
                [PdfHighlightAction.Load]: () =>
                    actions.handleLoad(pdfHighlights),
                [PdfHighlightAction.Highlight]: (
                    pdfHighlight: PdfHighlightProps
                ) => setHighlight(pdfHighlight.activityId),
            };

            if (!isReadOnly) {
                messageHandler[PdfHighlightAction.Upsert] = (
                    pdfHighlight: PdfHighlightProps
                ) => setEdit(pdfHighlight.activityId);
                messageHandler[PdfHighlightAction.Delete] = (
                    pdfHighlight: PdfHighlightProps
                ) =>
                    onAction(
                        "delete_note",
                        items.findIndex(
                            (item) =>
                                item.what === ActivityType.comment &&
                                item.id === pdfHighlight.activityId
                        )
                    );
            }

            return messageHandler;
        }, [setEdit, onAction, items, isReadOnly, actions, pdfHighlights])
    );

    // Update PDF when items change
    useEffect(() => {
        actions.handleLoad(pdfHighlights);
        return () => actions.handleLoad([]);
    }, [actions, pdfHighlights]);

    // Create handleHightlicht Callback Factory for ActivityDTOs
    const handleHighlight = useCallback(
        (item: ActivityDTO) => {
            const targetHighlight =
                item?.extras?.$type === ItemNoteType.PdfHighlight
                    ? item.id
                    : null;
            actions.handleHighlight({ activityId: targetHighlight });
            setHighlight(targetHighlight);
        },
        [actions]
    );

    return useMemo(
        () => [highlight, handleHighlight],
        [highlight, handleHighlight]
    );
};

const usePdfHighlightInput = (
    handleNoteExtras: (pdfHighlight: PdfHighlightProps) => void,
    noteRef: React.RefObject<{ clearableInput?: HTMLElement }>
) => {
    usePostMessageHandler(
        useMemo(
            () => ({
                [PdfHighlightAction.Select]: (
                    pdfHighlight: PdfHighlightProps
                ) => {
                    /* istanbul ignore else */
                    if (
                        pdfHighlight != null &&
                        noteRef.current &&
                        noteRef.current.clearableInput
                    ) {
                        noteRef.current.clearableInput.scrollIntoView({
                            block: "center",
                            inline: "center",
                        });
                        noteRef.current.clearableInput.focus();
                    }
                    handleNoteExtras(pdfHighlight);
                },
            }),
            [handleNoteExtras, noteRef]
        )
    );
    return usePdfHighlightActions();
};

export {
    usePdfHighlight,
    usePdfHighlightActions,
    usePdfHighlightActivityList,
    usePdfHighlightInput,
};
